import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { useLanguage } from '../../lib/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { Loader2, PieChart as PieChartIcon, TrendingUp, TrendingDown, PiggyBank, Wallet, Save, Sparkles, Target } from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from 'recharts';

const DEFAULT_CATEGORIES = ['alimentation', 'transport', 'logement', 'loisirs', 'sante', 'shopping', 'abonnements', 'autres'];

const PALETTE = ['#1e3a8a', '#2563eb', '#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#a855f7', '#ec4899', '#14b8a6'];

const monthShort = (ym) => {
  if (!ym) return '';
  const [y, m] = String(ym).split('-');
  const names = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc'];
  return `${names[(parseInt(m, 10) || 1) - 1]} ${String(y).slice(-2)}`;
};

export default function FinancePerso() {
  const { t } = useLanguage();
  const [dashboard, setDashboard] = useState(null);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingBudget, setEditingBudget] = useState(false);
  const [savingBudget, setSavingBudget] = useState(false);
  const [bForm, setBForm] = useState({ monthly_budget: '', categories: {} });

  const catLabel = (k) => t(`finance.cat.${k}`) || k;
  const incomeLabel = t('finance.incomeLabel');
  const expensesLabel = t('finance.expensesLabel');

  const load = async () => {
    setLoading(true);
    try {
      const [d, b] = await Promise.all([
        api.get('/client/finance-dashboard', { params: { months: 6 } }).catch(() =>
          api.get('/client/finance/dashboard', { params: { months: 6 } })
        ),
        api.get('/client/budget').catch(() => ({ data: null })),
      ]);
      setDashboard(d.data);
      setBudget(b.data || null);
      const cats = b.data?.categories || {};
      setBForm({
        monthly_budget: b.data?.monthly_budget || '',
        categories: DEFAULT_CATEGORIES.reduce((acc, k) => {
          acc[k] = cats[k] ?? '';
          return acc;
        }, {}),
      });
    } catch (e) {
      toast.error(t('finance.loadError'));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const saveBudget = async () => {
    setSavingBudget(true);
    try {
      const cats = {};
      Object.entries(bForm.categories).forEach(([k, v]) => {
        const n = parseFloat(v);
        if (!Number.isNaN(n) && n > 0) cats[k] = n;
      });
      await api.post('/client/budget', {
        monthly_budget: parseFloat(bForm.monthly_budget) || 0,
        categories: cats,
      });
      toast.success(t('finance.budgetSaved'));
      setEditingBudget(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('finance.genericError'));
    } finally {
      setSavingBudget(false);
    }
  };

  const monthlyData = useMemo(() => {
    if (!dashboard?.monthly) return [];
    return dashboard.monthly.map((m) => ({
      month: monthShort(m.month || m.ym || m.label),
      [incomeLabel]: Math.max(0, m.income ?? m.revenus ?? 0),
      [expensesLabel]: Math.abs(m.expenses ?? m.depenses ?? 0),
      Net: (m.net ?? ((m.income ?? 0) + (m.expenses ?? 0))),
    }));
  }, [dashboard, incomeLabel, expensesLabel]);

  const categoryData = useMemo(() => {
    const cats = dashboard?.current_month_by_category || dashboard?.categories || {};
    return Object.entries(cats)
      .filter(([k]) => k !== 'revenus')
      .map(([k, v]) => ({ name: catLabel(k), key: k, value: Math.abs(parseFloat(v) || 0) }))
      .filter((e) => e.value > 0)
      .sort((a, b) => b.value - a.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard, t]);

  const totalSpent = categoryData.reduce((s, c) => s + c.value, 0);
  const monthlyBudget = budget?.monthly_budget || 0;
  const budgetUsed = monthlyBudget > 0 ? Math.min(100, (totalSpent / monthlyBudget) * 100) : 0;

  const currentMonth = dashboard?.current || dashboard?.summary || {};
  const incomeNow = currentMonth.income ?? currentMonth.revenus ?? monthlyData[monthlyData.length - 1]?.[incomeLabel] ?? 0;
  const expenseNow = Math.abs(currentMonth.expenses ?? currentMonth.depenses ?? monthlyData[monthlyData.length - 1]?.[expensesLabel] ?? 0);
  const savingsRate = incomeNow > 0 ? Math.max(0, Math.min(100, ((incomeNow - expenseNow) / incomeNow) * 100)) : 0;

  if (loading) {
    return <div className="p-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto"/></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><PieChartIcon className="w-6 h-6 text-blue-700"/> {t('finance.title')}</h1>
          <p className="text-slate-500 text-sm mt-1 dark:text-slate-400">{t('finance.subtitle')}</p>
        </div>
        <Button variant="outline" onClick={() => setEditingBudget((v) => !v)}>
          <Target className="w-4 h-4 mr-1"/> {editingBudget ? t('finance.cancel') : (budget ? t('finance.editBudget') : t('finance.setBudget'))}
        </Button>
      </div>

      {/* KPI row */}
      <div className="grid md:grid-cols-4 gap-4">
        <Kpi label={t('finance.monthIncome')} value={incomeNow} icon={TrendingUp} accent="emerald" suffix=" €"/>
        <Kpi label={t('finance.monthExpenses')} value={expenseNow} icon={TrendingDown} accent="rose" suffix=" €"/>
        <Kpi label={t('finance.remaining')} value={incomeNow - expenseNow} icon={Wallet} accent="blue" suffix=" €"/>
        <KpiGauge label={t('finance.savingsRate')} value={savingsRate}/>
      </div>

      {/* Budget editor */}
      {editingBudget && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 dark:bg-slate-900 dark:border-slate-700">
          <h3 className="font-semibold flex items-center gap-2"><Target className="w-5 h-5 text-blue-700"/> {t('finance.myMonthlyBudget')}</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('finance.budgetHelp')}</p>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label>{t('finance.globalBudget')}</Label>
              <Input type="number" step="10" value={bForm.monthly_budget} onChange={(e) => setBForm({ ...bForm, monthly_budget: e.target.value })} data-testid="input-budget-global"/>
            </div>
            {DEFAULT_CATEGORIES.map((c) => (
              <div key={c}>
                <Label>{catLabel(c)} (€)</Label>
                <Input type="number" step="10" value={bForm.categories[c] ?? ''} onChange={(e) => setBForm({ ...bForm, categories: { ...bForm.categories, [c]: e.target.value } })} data-testid={`input-budget-${c}`}/>
              </div>
            ))}
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditingBudget(false)}>{t('finance.cancel')}</Button>
            <Button className="bg-blue-700" onClick={saveBudget} disabled={savingBudget} data-testid="btn-save-budget">
              {savingBudget ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Save className="w-4 h-4 mr-1"/>} {t('finance.save')}
            </Button>
          </div>
        </div>
      )}

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5 dark:bg-slate-900 dark:border-slate-700">
          <h3 className="font-semibold flex items-center gap-2 mb-3">{t('finance.incomeVsExpenses')}</h3>
          {monthlyData.length === 0 ? (
            <div className="text-slate-400 text-sm text-center py-8">{t('finance.noDataAvail')}</div>
          ) : (
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={monthlyData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2}/>
                  <XAxis dataKey="month" tick={{ fontSize: 12 }}/>
                  <YAxis tick={{ fontSize: 12 }}/>
                  <Tooltip formatter={(v) => `${(v || 0).toLocaleString('fr-FR')} €`} contentStyle={{ borderRadius: 8, fontSize: 12 }}/>
                  <Legend wrapperStyle={{ fontSize: 12 }}/>
                  <Bar dataKey={incomeLabel} fill="#10b981" radius={[4, 4, 0, 0]}/>
                  <Bar dataKey={expensesLabel} fill="#ef4444" radius={[4, 4, 0, 0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5 dark:bg-slate-900 dark:border-slate-700">
          <h3 className="font-semibold mb-3">{t('finance.expensesByCategory')}</h3>
          {categoryData.length === 0 ? (
            <div className="text-slate-400 text-sm text-center py-8">{t('finance.noExpenseThisMonth')}</div>
          ) : (
            <>
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={categoryData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={2}>
                      {categoryData.map((entry, idx) => (
                        <Cell key={entry.key} fill={PALETTE[idx % PALETTE.length]}/>
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `${(v || 0).toLocaleString('fr-FR')} €`} contentStyle={{ borderRadius: 8, fontSize: 12 }}/>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="mt-3 space-y-1 text-xs max-h-40 overflow-y-auto scrollbar-thin">
                {categoryData.map((c, idx) => (
                  <li key={c.key} className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: PALETTE[idx % PALETTE.length] }}></span>
                      {c.name}
                    </span>
                    <span className="font-medium">{c.value.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {/* Budget progress */}
      {monthlyBudget > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 dark:bg-slate-900 dark:border-slate-700">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-semibold flex items-center gap-2"><PiggyBank className="w-5 h-5 text-blue-700"/> {t('finance.monthlyBudget')}</h3>
            <div className="text-sm text-slate-600 dark:text-slate-300">
              <strong>{totalSpent.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</strong> / {monthlyBudget.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
            </div>
          </div>
          <Progress value={budgetUsed} className={`h-3 mt-3 ${budgetUsed >= 90 ? '[&>div]:bg-rose-500' : budgetUsed >= 70 ? '[&>div]:bg-amber-500' : '[&>div]:bg-emerald-500'}`}/>
          <div className="text-xs text-slate-500 mt-2 dark:text-slate-400">
            {budgetUsed >= 100 ? t('finance.budgetExceeded') : t('finance.remainingToSpend', { amount: Math.max(0, monthlyBudget - totalSpent).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) })}
          </div>

          {budget?.categories && (
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {Object.entries(budget.categories).map(([k, max]) => {
                const c = categoryData.find((x) => x.key === k);
                const spent = c?.value || 0;
                const pct = max > 0 ? Math.min(100, (spent / max) * 100) : 0;
                return (
                  <div key={k} className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium">{catLabel(k)}</span>
                      <span className="text-slate-500 dark:text-slate-400">{spent.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} / {parseFloat(max).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €</span>
                    </div>
                    <Progress value={pct} className={`h-1.5 mt-2 ${pct >= 90 ? '[&>div]:bg-rose-500' : pct >= 70 ? '[&>div]:bg-amber-500' : '[&>div]:bg-emerald-500'}`}/>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Premium teaser */}
      <div className="bg-gradient-to-br from-indigo-600 to-blue-700 text-white rounded-2xl p-5 flex items-start gap-3">
        <Sparkles className="w-6 h-6 shrink-0"/>
        <div>
          <div className="font-semibold">{t('finance.premiumTitle')}</div>
          <p className="text-sm text-blue-100 mt-1">{t('finance.premiumDesc')}</p>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent, suffix }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
    emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    rose: 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 dark:bg-slate-900 dark:border-slate-700">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors[accent]}`}>
          <Icon className="w-4 h-4"/>
        </div>
        <div className="text-slate-500 text-xs uppercase font-medium dark:text-slate-400">{label}</div>
      </div>
      <div className="text-2xl font-bold mt-3">
        {Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}{suffix}
      </div>
    </div>
  );
}

function KpiGauge({ label, value }) {
  const v = Math.max(0, Math.min(100, value));
  const color = v >= 20 ? 'text-emerald-600' : v >= 10 ? 'text-amber-600' : 'text-rose-600';
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 dark:bg-slate-900 dark:border-slate-700">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300">
          <PiggyBank className="w-4 h-4"/>
        </div>
        <div className="text-slate-500 text-xs uppercase font-medium dark:text-slate-400">{label}</div>
      </div>
      <div className={`text-2xl font-bold mt-3 ${color}`}>{v.toFixed(1)}%</div>
      <Progress value={v} className="h-1.5 mt-2"/>
    </div>
  );
}
